import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { SessionDeleteDialog } from '@/components/admin/sessions/session-delete-dialog';
import { ReplayPanel } from '@/components/admin/sessions/walk/replay-panel';
import { SessionHead } from '@/components/admin/sessions/walk/session-head';
import {
  SessionReading,
  type SessionReadingTab,
} from '@/components/admin/sessions/walk/session-reading';
import { SessionTiles } from '@/components/admin/sessions/walk/session-tiles';
import { sessionTrials } from '@/components/admin/sessions/walk/session-trials';
import { TrashedBanner } from '@/components/admin/sessions/walk/trashed-banner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError } from '@/lib/api';
import {
  NOYAU_TRIALS_KEY,
  SESSION_DETAIL_KEY,
  fetchNoyauTrials,
  fetchSessionDetail,
  restoreSession,
  trashSession,
} from '@/lib/session-api';
import { type HeadNoyauTarget, headNeedsTrials } from '@/lib/session-head';
import { useIsVitrine } from '@/lib/use-instance';
import { useReplaySettings } from '@/lib/use-replay-settings';
import { Spinner } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

/**
 * `/admin/sessions/<id>` - the PARCOURS of a session (§12.2).
 *
 * Opening a session opens its walk, page by page, in the order it traversed
 * them. That replaced the three-panel reader the spec used to describe, and the
 * grill of 2026-09-14 gives the reason in one sentence: a session is a timeline
 * of PAGES of which the protocol is only one, and a screen that opened the
 * conversation alone left a session without a turn on an empty page and showed
 * the values somebody typed far from where they typed them.
 *
 * ## One query for two screens
 *
 * `fetchSessionDetail` serves the parcours AND the conversation of §12.3, and
 * they share this key on purpose: a conversation opened from here must not
 * re-fetch what this page already holds. What the reading never carries is the
 * prompts served to the seats - they have their own route (§8.7), read on a
 * click by the screen that opens them.
 *
 * ## A trashed session opens, and reads
 *
 * §12.5: « une session en corbeille reste ouvrable en lecture sous un bandeau
 * qui donne sa date d'effacement ». So a 404 here is a session that never
 * existed, never one that was deleted - and the two get two sentences, because
 * there are two different things to do about them.
 *
 * ## Two tabs under the eight figures (#172)
 *
 * The head and the tiles are common; under them, Parcours and Variables
 * (`SessionReading`). The tab travels in the URL so a pasted link opens the
 * right one, and Parcours - the default - writes none.
 */

/** How often a session whose rangement is still running is read again. */
const RANGEMENT_POLL_MS = 3000;

/** How long the block the pill of the head led to stays ringed: the walk's own animation. */
const NOYAU_FLASH_MS = 1800;

/** What the screen carries in the address bar: only « variables » is ever written. */
type SessionWalkSearch = {
  tab?: 'variables' | undefined;
};

/**
 * `?tab=` as the router hands it over. `validateSearch` NEVER throws: an
 * unknown tab falls back on Parcours, never on a blank screen.
 */
export function readSearch(search: Record<string, unknown>): SessionWalkSearch {
  return search.tab === 'variables' ? { tab: 'variables' } : {};
}

/** The search a tab writes: none for Parcours, the default. */
export function searchOfTab(tab: SessionReadingTab): SessionWalkSearch {
  return tab === 'variables' ? { tab: 'variables' } : {};
}

export const Route = createFileRoute('/admin/sessions/$sessionId')({
  validateSearch: readSearch,
  component: SessionWalkPage,
});

function SessionWalkPage() {
  const { sessionId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  // The vitrine makes no paid call: the two dry trials give way to a note that
  // sends the operator to form.dev, on the session once it has gone up
  // (docs/specs/vitrine.md section 7, H8).
  const vitrine = useIsVitrine();
  // « Créer un NPC » (npc.md §13.3) is drawn only where the NPC exist: the replay open - the probe
  // of the rail, under the same key - and not the vitrine, which has none.
  const npcAvailable = useReplaySettings().data != null && !vitrine;

  const detail = useQuery({
    queryKey: SESSION_DETAIL_KEY(email, sessionId),
    queryFn: () => fetchSessionDetail(sessionId),
    retry: false,
    // The ranger of a session still running (scenario-succes.md §7.2, « en cours »)
    // settles by itself in the background: the page reads again until it has.
    refetchInterval: (query) =>
      query.state.status !== 'error' && query.state.data?.rangement?.state === 'pending'
        ? RANGEMENT_POLL_MS
        : false,
  });

  /*
    The pill of the head (#218) leads to a block of Parcours: the tab is asked for first, and the
    reading scrolls once it is drawn. The ring is dropped after its animation, so a reload of
    the query never leaves a block ringed.
  */
  const [focusNoyau, setFocusNoyau] = useState<HeadNoyauTarget | null>(null);

  /*
    The line « Lu à blanc » of the head lends the notes of its principal from the trials of the
    session - the same list, and the same key, as the section below. Asked for only when the
    line is one: a retained noyau, or none at all, needs no trial.
  */
  const needsTrials = detail.data !== undefined && headNeedsTrials(detail.data.session) && !vitrine;
  const trials = useQuery({
    queryKey: NOYAU_TRIALS_KEY(sessionId),
    queryFn: () => fetchNoyauTrials(sessionId),
    refetchOnWindowFocus: false,
    enabled: needsTrials,
  });

  const rejected = useRejectOn401(detail.error);
  const missing = detail.error instanceof ApiError && detail.error.status === 404;

  /*
    Both gestures re-read rather than patching the cache: the trash writes a
    stamp the server owns (`deletedAt`), and the banner prints it. A local
    `new Date()` would put a date on screen that the next reload contradicts.

    TWO keys are invalidated, and the second is not tidiness. « Afficher la
    corbeille » SWAPS the list (§12.4), so a session trashed from here leaves
    one list and joins the other: a list left alone would keep showing a row
    that is no longer in it, on the screen the breadcrumb goes back to.
  */
  const reread = (): void => {
    void queryClient.invalidateQueries({ queryKey: SESSION_DETAIL_KEY(email, sessionId) });
    void queryClient.invalidateQueries({ queryKey: ['sessions', email] });
  };

  const trash = useMutation({
    mutationFn: () => trashSession(sessionId),
    onSuccess: () => {
      setConfirming(false);
      reread();
    },
  });

  const restore = useMutation({
    mutationFn: () => restoreSession(sessionId),
    onSuccess: reread,
  });

  const tab: SessionReadingTab = search.tab === 'variables' ? 'variables' : 'parcours';

  useEffect(() => {
    if (focusNoyau === null || tab !== 'parcours') return;
    const timer = window.setTimeout(() => setFocusNoyau(null), NOYAU_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [focusNoyau, tab]);
  const selectTab = (next: SessionReadingTab): void => {
    void navigate({
      to: '/admin/sessions/$sessionId',
      params: { sessionId },
      search: searchOfTab(next),
      replace: true,
    });
  };

  /*
    Where the two dry trials stand (#224): in the foot of their block, or - for a session without
    the block - in the group under the fil; the vitrine's note takes the place of the buttons.
  */
  const placed =
    detail.data === undefined
      ? null
      : sessionTrials({
          detail: detail.data,
          email,
          vitrine,
          ringNoyauTrial: focusNoyau === 'trial',
        });

  const openNoyau = (target: HeadNoyauTarget): void => {
    setFocusNoyau(target);
    if (tab !== 'parcours') selectTab('parcours');
  };

  return (
    <section data-testid={SESSIONS_TEST_IDS.walk} className="flex flex-col gap-5">
      {detail.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner label="Chargement de la session" />
        </div>
      ) : null}

      {missing ? <p className="text-gray-700">Aucune session sous cet identifiant.</p> : null}
      {detail.isError && !missing && !rejected ? (
        <p role="alert" className="text-gray-700">
          Cette session n’a pas pu être lue. Rechargez la page.
        </p>
      ) : null}

      {detail.data !== undefined && placed !== null ? (
        <>
          {detail.data.session.deletedAt === null ? null : (
            <TrashedBanner
              deletedAt={detail.data.session.deletedAt}
              busy={restore.isPending}
              onRestore={() => restore.mutate()}
            />
          )}

          <SessionHead
            detail={detail.data}
            onDelete={() => setConfirming(true)}
            createNpc={npcAvailable}
            trials={trials.data?.trials ?? []}
            onOpenNoyau={openNoyau}
          />
          {/*
            « Rejeu par Claude » (rejeu.md §12, #193): under the head and BEFORE
            the figures, on a session Claude played in the place of a person.
          */}
          {detail.data.replay === undefined || detail.data.replay === null ? null : (
            <ReplayPanel
              sessionId={detail.data.session.id}
              replay={detail.data.replay}
              status={detail.data.session.status}
              deviceCostEur={detail.data.session.costEur}
              modelsVersion={detail.data.settings?.models?.number ?? null}
            />
          )}
          <SessionTiles detail={detail.data} />

          <SessionReading
            detail={detail.data}
            tab={tab}
            onSelectTab={selectTab}
            focusNoyau={focusNoyau}
            tocTrials={placed.toc}
            noyauTrial={placed.noyauTrial}
            rangementTrial={placed.rangementTrial}
            /*
              The dry trials (noyau.md §9.4, #164; scenario-succes.md §7.3, #177): each is the tool
              of its block and stands in its foot since #224 - a session that has the protocol but
              not the block keeps them here, under the group « Essais à blanc sur cette session »
              (#220), and on the vitrine the note takes the place of the buttons.
            */
            trial={placed.group}
          />
        </>
      ) : null}

      {/*
        La MÊME boîte que la liste, montée et non recopiée. Une copie a divergé
        sur quatre points - le titre, le libellé de confirmation, un ton
        `destructive` pour un geste que « Restaurer » défait pendant trente
        jours, et une phrase de purge qui oubliait les variables là où §12.5 en
        exige trois : les tours, les variables, les entrées servies.
      */}
      {confirming && detail.data ? (
        <SessionDeleteDialog
          row={detail.data.session}
          busy={trash.isPending}
          error={trash.isError ? 'La suppression a échoué. Réessayez.' : null}
          onConfirm={() => trash.mutate()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </section>
  );
}
